import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type ClientStatus = 'active' | 'trial' | 'expired' | 'blocked';
export type ClientDesignation = 'sarpanch' | 'wardpanch' | string;

export interface ClientOrderItem {
  order_id: string;
  plan_id: string;
  plan_name: string | null;
  amount_paid: string; // ₹ amount, string e.g. "199.00"
  status: 'created' | 'paid' | 'failed' | string;
  created_at: string;
  paid_at: string | null;
  valid_until: string | null;
}

export interface ClientBlockInfo {
  is_blocked: boolean;
  blocked_at: string | null;
  unblocked_at: string | null;
}

export interface ClientItem {
  id: string; // UUID
  name: string | null;
  phone: string;
  // The old town / gram panchayat, else the most local registration place (village, samiti, district)
  city: string;
  // VOTER_SLIP_WARD_ORDERS §15 (same naming rules as GET /owner/subscriptions); null when not given
  email?: string | null;
  village?: string | null;
  samiti?: string | null;
  district?: string | null;
  state?: string | null;
  // Raw role_type from the app, not normalised; null when the profile is empty
  designation: ClientDesignation | null;
  // post (or role_type) in lowercase: sarpanch / wardpanch, "agent" for a demo agent without profile
  post: string | null;
  position_title: string | null;
  registered_at: string | null;
  status: ClientStatus;
  plan_id: string | null;
  plan_name: string | null;
  plan_expires_at: string | null;
  // Migration 0035: set while soft-deleted, null otherwise
  deleted_at?: string | null;
  // The election date the client set in the app ("YYYY-MM-DD"), its poll times ("HH:MM:SS") and label;
  // null when none is set. election_own: true = the client typed it, false = one of the owner's dates
  election_date?: string | null;
  election_time_from?: string | null;
  election_time_to?: string | null;
  election_label?: string | null;
  election_own?: boolean | null;

  // Details fields
  orders?: ClientOrderItem[];
  block?: ClientBlockInfo | null;

  // UI helper aliases
  candidateName?: string;
  constituency?: string;
  registeredDate?: string;
  subscriptionExpiry?: string;
  totalSpent?: number;
  notes?: string;
}

export interface ClientState {
  clients: ClientItem[];
  selectedClient: ClientItem | null;
  filterStatus: 'all' | ClientStatus;
  searchQuery: string;
  loading: boolean;
  error: string | null;
}

export const normalizeClient = (item: any): ClientItem => {
  const id = String(item.id || item.admin_id || '');
  const name = item.name !== undefined ? item.name : item.candidateName || null;
  const phone = item.phone || '';
  const city = item.city || item.constituency || '';
  const designation = item.designation || null;
  const post = item.post || null;
  const position_title = item.position_title || null;
  const registered_at = item.registered_at || item.registeredDate || null;
  const status: ClientStatus = item.status || 'trial';
  const plan_id = item.plan_id || item.planId || null;
  const plan_name = item.plan_name || item.planName || null;
  const plan_expires_at = item.plan_expires_at || item.subscriptionExpiry || null;
  const orders = Array.isArray(item.orders) ? item.orders : [];
  const block = item.block !== undefined ? item.block : null;
  const deleted_at = item.deleted_at ?? null;
  const email = item.email || null;
  const village = item.village || null;
  const samiti = item.samiti || null;
  const district = item.district || null;
  const state = item.state || null;

  return {
    id,
    name,
    phone,
    city,
    email,
    village,
    samiti,
    district,
    state,
    designation,
    post,
    position_title,
    registered_at,
    status,
    plan_id,
    plan_name,
    plan_expires_at,
    orders,
    block,
    deleted_at,

    // UI helpers
    candidateName: name || 'Unnamed Candidate',
    constituency: city || 'N/A',
    registeredDate: registered_at ? new Date(registered_at).toLocaleDateString() : 'N/A',
    subscriptionExpiry: plan_expires_at ? new Date(plan_expires_at).toLocaleDateString() : 'Never Subscribed',
    totalSpent: orders.reduce((sum: number, o: any) => sum + (parseFloat(o.amount_paid) || 0), 0),
    notes: block?.is_blocked ? `Blocked at ${new Date(block.blocked_at!).toLocaleString()}` : '',
  };
};

// "Rampura, Sanganer, Jaipur": village, samiti, district (Zila Parishad clients have only the district)
export const clientPlaceText = (c: Pick<ClientItem, 'village' | 'samiti' | 'district'>): string =>
  [c.village, c.samiti, c.district].filter(Boolean).join(', ');

const initialState: ClientState = {
  clients: [],
  selectedClient: null,
  filterStatus: 'all',
  searchQuery: '',
  loading: false,
  error: null,
};

const clientSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    setClientLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setClientError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setClients: (state, action: PayloadAction<ClientItem[]>) => {
      state.clients = (action.payload || []).map(normalizeClient);
      state.loading = false;
    },
    setSelectedClient: (state, action: PayloadAction<ClientItem | null>) => {
      state.selectedClient = action.payload ? normalizeClient(action.payload) : null;
      state.loading = false;
    },
    setFilterStatus: (state, action: PayloadAction<'all' | ClientStatus>) => {
      state.filterStatus = action.payload;
    },
    updateClientBlockStatusReducer: (
      state,
      action: PayloadAction<{ id: string; status: ClientStatus; block?: ClientBlockInfo | null }>
    ) => {
      const client = state.clients.find((c) => c.id === action.payload.id);
      if (client) {
        client.status = action.payload.status;
        if (action.payload.block !== undefined) {
          client.block = action.payload.block;
        }
      }
      if (state.selectedClient && state.selectedClient.id === action.payload.id) {
        state.selectedClient.status = action.payload.status;
        if (action.payload.block !== undefined) {
          state.selectedClient.block = action.payload.block;
        }
      }
    },
  },
});

export const {
  setClientLoading,
  setClientError,
  setClients,
  setSelectedClient,
  setFilterStatus,
  updateClientBlockStatusReducer,
} = clientSlice.actions;

export default clientSlice.reducer;
